// Mirrors impire-sdk-go/state/wire.go and state/bodies.go — the state
// service's wire as the library carries it (02-DESIGN/10-state/01-stores-types-and-instances.md,
// 02-the-state-service.md, 03-indexes.md; decision 0015): the family's
// subjects, the store, type and index bodies, the operation record, the
// grammar of names and paths, the error codes. Re-exported here so that a
// surface imports one wire module per Go file. The family is account-local:
// nothing serves it until the tenant's state service runs, and a request
// before then finds no service.

import { state } from "@impire-io/sdk";

/** SERVICE is the state service's micro service name, under which callers in the tenant discover its sealing key. */
export const SERVICE = state.SERVICE;

/** The verbs and the acts under them. */
export const Verb = state.Verb;
export const Act = state.Act;

/** OP_SNAPSHOT is the one operation type the platform itself understands. */
export const OP_SNAPSHOT = state.OP_SNAPSHOT;

/** The error codes. */
export const Code = state.Code;

/** validName reports whether s is a name of the grammar: `[a-z0-9-]+`. */
export const validName = state.validName;

/** validStoreName reports whether s is a store name: a name that is not reserved. */
export const validStoreName = state.validStoreName;

/** RESERVED are the store names refused at creation. */
export const RESERVED = state.RESERVED;

/** validPath reports whether s is an instance path: one or more names joined by slashes. */
export function validPath(s: string): boolean {
  try {
    state.parsePath(s);
    return true;
  } catch {
    return false;
  }
}

/** typeOf is the first token of a path: the type it names, when it resolves. */
export const typeOf = state.typeOf;

/** The bodies. */
export const HistoryPolicy = state.HistoryPolicy;
export type HistoryPolicy = state.HistoryPolicy;
export type StoreRequest = state.StoreRequest;
export type StoreUpdate = state.StoreUpdate;
export type Store = state.Store;
/** WhenFull is what a store does at its size: refuse new changes, or make room by keeping only current state (decision 0050). */
export const WhenFull = state.WhenFull;
export type WhenFull = state.WhenFull;

/**
 * DEFAULT_BUDGET is the size a store is created with when its request names
 * none: the state service's reference default, 256 MiB (impire-sdk-go
 * state.DefaultBudget, 02-DESIGN/10-state/02-the-state-service.md § reference
 * defaults); MIN_BUDGET its floor; NEAR_FULL the fraction past which a store
 * is near full; MAX_SNAPSHOT the inline limit of one snapshot.
 */
export const DEFAULT_BUDGET = state.DEFAULT_BUDGET;
export const MIN_BUDGET = state.MIN_BUDGET;
export const NEAR_FULL = state.NEAR_FULL;
export const MAX_SNAPSHOT = state.MAX_SNAPSHOT;
export type StoreListReply = state.StoreListReply;
export const Effect = state.Effect;
export type Effect = state.Effect;
export type OperationDef = state.OperationDef;
export type TypeRecord = state.TypeRecord;
export type NamedType = state.NamedType;
export type TypeListReply = state.TypeListReply;
export const IndexKind = state.IndexKind;
export type IndexKind = state.IndexKind;
export const IndexSource = state.IndexSource;
export type IndexSource = state.IndexSource;
export type IndexConfig = state.IndexConfig;
export type IndexDeclaration = state.IndexDeclaration;
export type NamedIndex = state.NamedIndex;
export type IndexListReply = state.IndexListReply;

/** The record and what the reads answer. */
export type Operation = state.Operation;
export type Changes = state.Changes;
export type Current = state.Current;
export type Instance = state.Instance;
/** Item is one entry of the store-wide list: the path, the type it resolves to (absent where untyped), and its last change. */
export type Item = state.Item;
/** ItemPage is one page of the store-wide list, with every item counted and the path to ask after for the next. */
export type ItemPage = state.ItemPage;

/** SearchQuery is a search index's query: the words, and how many hits the caller wants. */
export type SearchQuery = state.SearchQuery;
/** Hit is one result of a query: an item and its score. An index is never authority. */
export type Hit = state.Hit;
/** SearchResult is a collected query: the hits, best first, and the count of everything that matched. */
export type SearchResult = state.SearchResult;

/** The subjects, built by the library. */
export const storeSubject = state.storeSubject;
export const storeListSubject = state.storeListSubject;
export const typeSubject = state.typeSubject;
export const typeListSubject = state.typeListSubject;
export const indexSubject = state.indexSubject;
export const indexListSubject = state.indexListSubject;
export const instanceSubject = state.instanceSubject;
export const listSubject = state.listSubject;
export const storeItemsSubject = state.storeItemsSubject;

/**
 * typeRecordText renders a type's record as the console edits it: the four
 * facets, the revision left out because the service sets it.
 */
export function typeRecordText(rec: TypeRecord): string {
  const out: Record<string, unknown> = {};
  if (rec.schema !== undefined) {
    out.schema = rec.schema;
  }
  if (rec.history !== undefined) {
    out.history = rec.history;
  }
  if (rec.children !== undefined) {
    out.children = rec.children;
  }
  if (rec.operations !== undefined) {
    out.operations = rec.operations;
  }
  return JSON.stringify(out, null, 2) + "\n";
}

/** parseTypeRecord reads a type's record from text; an Error names what is wrong with it. */
export function parseTypeRecord(text: string): TypeRecord | Error {
  let v: unknown;
  try {
    v = JSON.parse(text);
  } catch (err) {
    return new Error(`not JSON: ${err instanceof Error ? err.message : String(err)}`);
  }
  if (typeof v !== "object" || v === null || Array.isArray(v)) {
    return new Error("a type is a JSON object with schema, history, children and operations");
  }
  const o = v as Record<string, unknown>;
  const rec: TypeRecord = {};
  if (o.schema !== undefined) {
    rec.schema = o.schema;
  }
  if (o.history !== undefined) {
    if (o.history !== HistoryPolicy.compactable && o.history !== HistoryPolicy.full) {
      return new Error(`history is "compactable" or "full", not ${JSON.stringify(o.history)}`);
    }
    rec.history = o.history;
  }
  if (o.children !== undefined) {
    if (typeof o.children !== "object" || o.children === null || Array.isArray(o.children)) {
      return new Error("children maps a name to a type");
    }
    for (const [k, t] of Object.entries(o.children)) {
      if (typeof t !== "string" || !validName(k) || !validName(t)) {
        return new Error(`children: ${k} must name a type, lowercase letters, digits and hyphens`);
      }
    }
    rec.children = o.children as Record<string, string>;
  }
  if (o.operations !== undefined) {
    if (typeof o.operations !== "object" || o.operations === null || Array.isArray(o.operations)) {
      return new Error("operations maps an operation's type to its schema and effect");
    }
    for (const [k, def] of Object.entries(o.operations)) {
      if (typeof def !== "object" || def === null) {
        return new Error(`operations: ${k} is an object with schema and effect`);
      }
      const e = (def as Record<string, unknown>).effect;
      if (e !== undefined && e !== Effect.none && e !== Effect.merge) {
        return new Error(`operations: ${k}: effect is "none" or "merge"`);
      }
    }
    rec.operations = o.operations as Record<string, OperationDef>;
  }
  return rec;
}
